import { type Lang } from "@/lib/i18n";
import { type SubPageContent } from "@/components/common/SubPageLayout";

const THREE_LAYER_IMAGE =
  "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sintered_five_layer_mesh_structure.png";
const MULTI_LAYER_IMAGE =
  "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-precision-s-cb8314.png";
const FIVE_LAYER_IMAGE =
  "https://mutualfilter.com/wp-content/uploads/2026/09/Sintered-Mesh-Filter-Elements-of-alloyfilter.jpg?v=hy0enjdkewc";
const MULTILAYER_HERO_IMAGE =
  "https://mutualfilter.com/wp-content/uploads/2026/09/Multi-Layer-Sintered-Woven-Wire-Mesh-Discs.jpg";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "ThreeLayerSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Sintered Multilayer Mesh",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "3 Layer Sintered Wire Mesh",
      lead: "Engineered with a protective outer mesh, a micro-aperture control layer, and a rigid structural support mesh, balancing high flow permeability with dependable mechanical support.",
      secondaryCta: {
        label: "Multilayer Overview",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: THREE_LAYER_IMAGE,
        alt: "Cross section of 3-layer sintered stainless steel mesh laminate",
      },
      cardNote: "3-Layer Laminate Structure",
      cardLink: {
        label: "Request Specifications",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Filtration Rating", value: "2 – 100 µm" },
        { label: "Standard Alloys", value: "AISI 316L / 304L" },
        { label: "Nominal Thickness", value: "1.70 mm (0.8 – 3.5 mm)" },
        { label: "Layer Construction", value: "Protection + Control + Support" },
      ],
    },
    overview: {
      eyebrow: "Three-Stage Laminate",
      heading: "High-Permeability Three-Layer Filtration Matrix",
      paragraphs: [
        "The 3-layer sintered wire mesh structure is engineered for applications where elevated flow permeability and lower initial clean pressure drop are vital alongside micron particle retention. The laminate condenses the filtration mechanism into three diffusion-bonded functional layers.",
        "The outer protection layer acts as a physical pre-strainer and protects the central fine woven wire cloth from coarse particulate impact. The central control layer provides precise 2 µm to 100 µm particle cut-off. Beneath it, a heavier backing mesh delivers essential rigidity and open area for unhindered process discharge.",
        "Vacuum diffusion bonding welds all wire junctions into a stable matrix, allowing the composite to endure forward process cycles and high-velocity reverse flow without layer separation or aperture distortion.",
      ],
      keyPoints: [
        "Protective outer mesh shields the delicate micron-level precision control layer.",
        "Optimized 3-tier geometry maintains higher fluid permeability than denser multi-layer plates.",
        "Solid-state diffusion eliminates adhesive contamination and wire shedding.",
      ],
      image: {
        src: THREE_LAYER_IMAGE,
        alt: "Cross section of 3-layer sintered stainless steel mesh laminate",
      },
    },
    specTable: {
      heading: "Standard 3-Layer Specifications",
      intro: "Standard AISI 316L and 304L three-layer configurations for liquid and gas filtration assemblies.",
      columns: [
        { key: "specification", label: "Specification", align: "left" },
        { key: "nominalRating", label: "Nominal Rating (µm)", align: "right" },
        { key: "controlMesh", label: "Control Layer Mesh", align: "right" },
        { key: "nominalThickness", label: "Nominal Thickness (mm)", align: "right" },
        { key: "layerOrder", label: "Layer Stacking Sequence", align: "left" },
      ],
      rows: [
        {
          specification: "3L-005",
          nominalRating: "5",
          controlMesh: "Dutch Weave",
          nominalThickness: "1.70",
          layerOrder: "Protection (Square) + Control (Dutch) + Base Grid",
        },
        {
          specification: "3L-010",
          nominalRating: "10",
          controlMesh: "Dutch Weave",
          nominalThickness: "1.70",
          layerOrder: "Protection (Square) + Control (Dutch) + Base Grid",
        },
        {
          specification: "3L-025",
          nominalRating: "25",
          controlMesh: "500 Mesh",
          nominalThickness: "1.70",
          layerOrder: "Protection (100M) + Control (500M) + Base (20M)",
        },
        {
          specification: "3L-050",
          nominalRating: "50",
          controlMesh: "300 Mesh",
          nominalThickness: "1.70",
          layerOrder: "Protection (80M) + Control (300M) + Base (20M)",
        },
        {
          specification: "3L-100",
          nominalRating: "100",
          controlMesh: "150 Mesh",
          nominalThickness: "1.70",
          layerOrder: "Protection (60M) + Control (150M) + Base (16M)",
        },
      ],
      caption: "Thicknesses between 0.8 mm and 3.5 mm can be customized by selecting alternate backing meshes.",
    },
    features: {
      heading: "Design and Operational Advantages",
      intro: "A focused configuration combining reduced flow resistance with robust mechanical support.",
      items: [
        {
          title: "High Flow Permeability",
          text: "Featuring fewer total internal wire interfaces than 5-layer plates, 3-layer laminates allow higher throughput with minimal clean differential pressure.",
        },
        {
          title: "Stable Pore Geometry",
          text: "Solid-state bonding keeps wire intersections locked in place, ensuring precise particle cut-off even under fluctuating process pressures.",
        },
        {
          title: "Clean Backwash Performance",
          text: "The streamlined 3-tier matrix minimizes depth particle entrapment, promoting efficient surface backwashing and cleaning.",
        },
        {
          title: "Versatile Forming Options",
          text: "Readily sheared, stamped, rolled, and welded into cylindrical candles, basket liners, and circular filter discs.",
        },
      ],
    },
    related: {
      heading: "Related Sintered Mesh Constructions",
      intro: "Compare 3-layer laminates with standard 5-layer and reinforced multi-layer composites across our sintered product line.",
      cards: [
        {
          title: "Sintered Multilayer Mesh",
          text: "The parent family encompassing standard 3-layer, 5-layer, and multi-tier diffusion-bonded composite laminates.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "Vacuum diffusion bonded sintered multilayer wire mesh laminate",
          },
          badge: "Parent Category",
          linkLabel: "View Multilayer Overview",
        },
        {
          title: "5 Layer Sintered Mesh",
          text: "The universal industrial configuration combining protection, control, dispersion, and dual skeleton layers for maximum stability.",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "5-layer standard sintered wire mesh laminate structure",
          },
          badge: "5-Layer Standard",
          linkLabel: "View 5-Layer Specifications",
        },
        {
          title: "Multi Layer Sintered Mesh",
          text: "Vacuum-sintered composites combined with rigid perforated plates or heavy reinforcement meshes for demanding service.",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "Macro view of multi-layer sintered stainless steel wire mesh laminate",
          },
          badge: "Heavy Reinforcement",
          linkLabel: "View Multi-Layer Specifications",
        },
      ],
    },
    cta: {
      id: "three-layer-sintered-cta",
      eyebrow: "Custom Fabrication",
      heading: "Order Custom 3-Layer Sintered Wire Mesh",
      lead: "MutualFilter manufactures 3-layer sintered mesh sheets, stamped circles, and seamless filter tubes fabricated to your required micron rating and dimensional tolerances.",
      secondaryLabel: "Contact Our Engineers",
      secondaryTo: "/ContactPage",
      note: "Constructed in AISI 316L and 304L stainless steels. Complete material certificates available upon request.",
    },
  },
  zh: {
    breadcrumbKey: "ThreeLayerSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "多层烧结复合网",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "3 层烧结不锈钢丝网",
      lead: "由保护网、微米级精滤网与重型结构支撑网构成，兼顾高通量渗透性能与可靠的机械抗压能力。",
      secondaryCta: {
        label: "多层烧结网总览",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: THREE_LAYER_IMAGE,
        alt: "三层烧结不锈钢丝网微观截面与结构层析",
      },
      cardNote: "3 层复合结构",
      cardLink: {
        label: "获取技术参数",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "过滤精度", value: "2 – 100 µm" },
        { label: "标准材质", value: "AISI 316L / 304L" },
        { label: "公称厚度", value: "1.70 mm (0.8 – 3.5 mm)" },
        { label: "结构组成", value: "保护层 + 精度控制层 + 支撑层" },
      ],
    },
    overview: {
      eyebrow: "三级复合构造",
      heading: "兼顾大通量与微米级精度的三层烧结基底",
      paragraphs: [
        "3 层烧结丝网专为追求更低初始流动阻力与高渗透率的工况开发。相较于 5 层或更多层的密实结构，该配置将流体过滤功能精炼为三层经过扩散焊接的专用金属网层。",
        "外侧保护层不仅作为前置粗滤拦截大颗粒冲击，更对内部精滤丝网提供机械屏障；中间控制层根据工艺要求采用平纹或荷兰密纹网，确保 2 µm 至 100 µm 范围内的稳定截留；底部粗目网则形成稳固的流通导流道与机械承托。",
        "高温真空固态扩散熔接工艺消除了丝网脱层隐患，在周期性脉冲反洗或介质交变流动下，孔径大小与排列间距始终保持稳定。",
      ],
      keyPoints: [
        "外层保护网对精密微米级控制层提供坚固的物理屏障与耐磨保护。",
        "流道比 5 层结构更加精简，具备更优异的介质渗透通量与较低初始压降。",
        "固相扩散焊接杜绝了胶黏剂污染与钢丝脱落风险。",
      ],
      image: {
        src: THREE_LAYER_IMAGE,
        alt: "三层烧结不锈钢丝网微观截面与结构层析",
      },
    },
    specTable: {
      heading: "3 层烧结丝网规格参数",
      intro: "提供 AISI 316L 及 304L 牌号，适用于石化气液过滤、制药粉体截留与工业水处理系统。",
      columns: [
        { key: "specification", label: "规格型号", align: "left" },
        { key: "nominalRating", label: "公称精度 (µm)", align: "right" },
        { key: "controlMesh", label: "控制层目数", align: "right" },
        { key: "nominalThickness", label: "公称厚度 (mm)", align: "right" },
        { key: "layerOrder", label: "层级叠合排列", align: "left" },
      ],
      rows: [
        {
          specification: "3L-005",
          nominalRating: "5",
          controlMesh: "荷兰密纹网",
          nominalThickness: "1.70",
          layerOrder: "保护网 (平纹) + 控制层 (密纹) + 粗目支撑网",
        },
        {
          specification: "3L-010",
          nominalRating: "10",
          controlMesh: "荷兰密纹网",
          nominalThickness: "1.70",
          layerOrder: "保护网 (平纹) + 控制层 (密纹) + 粗目支撑网",
        },
        {
          specification: "3L-025",
          nominalRating: "25",
          controlMesh: "500 目",
          nominalThickness: "1.70",
          layerOrder: "保护网 (100目) + 控制层 (500目) + 支撑网 (20目)",
        },
        {
          specification: "3L-050",
          nominalRating: "50",
          controlMesh: "300 目",
          nominalThickness: "1.70",
          layerOrder: "保护网 (80目) + 控制层 (300目) + 支撑网 (20目)",
        },
        {
          specification: "3L-100",
          nominalRating: "100",
          controlMesh: "150 目",
          nominalThickness: "1.70",
          layerOrder: "保护网 (60目) + 控制层 (150目) + 支撑网 (16目)",
        },
      ],
      caption: "根据客户不同承压与安装厚度要求，厚度可在 0.8 mm 至 3.5 mm 范围内调整。",
    },
    features: {
      heading: "核心设计与工程应用优势",
      intro: "集精细截留、高通量及抗反洗疲劳于一体的高效多孔板材。",
      items: [
        {
          title: "优异的流体渗透性",
          text: "减少了内部界面的流动阻力，在同等过滤精度下拥有更高的单位面积处理通量。",
        },
        {
          title: "稳定的微孔孔径",
          text: "固态扩散结合牢固锁死各网层交叉点，在工作压力波动下不会发生网丝位移与孔径变形。",
        },
        {
          title: "高效的反冲洗再生",
          text: "结构层数精简，微孔深度相对较浅，颗粒易于在反吹或反洗过程中快速脱附排出。",
        },
        {
          title: "灵活的机加工性能",
          text: "易于裁切、冲压成圆片、滚筒卷制及精密氩弧焊接成各种规格的滤芯滤管。",
        },
      ],
    },
    related: {
      heading: "相关烧结复合网构型",
      intro: "对比 3 层复合网与 5 层标准板及加厚多层复合网，为您的系统选择最佳构型。",
      cards: [
        {
          title: "多层烧结丝网总览",
          text: "涵盖 3 层、5 层及多层冲孔复合网在内的全系列高温真空固相扩散焊接产品。",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "高温真空扩散焊接多层烧结不锈钢丝网复合板",
          },
          badge: "上级系列",
          linkLabel: "查看多层烧结网总览",
        },
        {
          title: "5 层标准烧结网",
          text: "集保护层、精度控制层、分流缓冲层及双层加强骨架网于一体，具备卓越的结构刚度。",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "五层标准不锈钢烧结丝网复合板",
          },
          badge: "5 层标准",
          linkLabel: "查看 5 层规格",
        },
        {
          title: "多层定制复合烧结网",
          text: "采用多层丝网与冲孔基板紧密烧结，具备极高抗爆破强度，胜任重载固液分离工况。",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "多层不锈钢烧结丝网微观复合结构与扩散焊接截面",
          },
          badge: "重载复合",
          linkLabel: "查看多层规格",
        },
      ],
    },
    cta: {
      id: "three-layer-sintered-cta",
      eyebrow: "来图打样与规格定制",
      heading: "获取 3 层烧结丝网工程报价",
      lead: "互拓丝网提供各种厚度与精度的 3 层烧结板材、异形冲压滤片及卷焊滤筒，支持小批量定制与批量供货。",
      secondaryLabel: "技术咨询与支持",
      secondaryTo: "/ContactPage",
      note: "材质提供 AISI 316L、304L，按订单要求提供材质检验证明及出厂检测记录。",
    },
  },
  es: {
    breadcrumbKey: "ThreeLayerSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Malla multicapa sinterizada",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malla sinterizada de 3 capas",
      lead: "Estructurada con malla exterior protectora, capa de control micrométrica y malla de soporte rígida, equilibrando alto caudal con soporte mecánico confiable.",
      secondaryCta: {
        label: "Resumen multicapa",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: THREE_LAYER_IMAGE,
        alt: "Sección transversal de malla sinterizada de 3 capas",
      },
      cardNote: "Laminado de 3 capas",
      cardLink: {
        label: "Solicitar especificaciones",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Retención", value: "2 – 100 µm" },
        { label: "Aleaciones estándar", value: "AISI 316L / 304L" },
        { label: "Espesor nominal", value: "1.70 mm (0.8 – 3.5 mm)" },
        { label: "Estructura", value: "Control + Protección + Soporte" },
      ],
    },
    overview: {
      eyebrow: "Estructura de 3 etapas",
      heading: "Matriz de filtración de 3 capas con alta permeabilidad",
      paragraphs: [
        "La malla sinterizada de 3 capas está diseñada para aplicaciones donde la alta permeabilidad y una baja caída inicial de presión son primordiales junto a la retención micrométrica.",
        "La capa exterior protege el tejido fino contra abrasión mecánica; la capa central de control define el corte preciso entre 2 µm y 100 µm; y la capa de base aporta la resistencia estructural necesaria para la evacuación del fluido.",
        "La unión por difusión térmica fusiona todos los contactos en una sola placa indeformable, capaz de soportar flujos directos y retrolavados intensos sin alteración de apertura.",
      ],
      keyPoints: [
        "Malla exterior protectora que resguarda la capa de control de precisión.",
        "Geometría optimizada de 3 niveles con mayor permeabilidad que placas más densas.",
        "La difusión en estado sólido descarta el desprendimiento de fibras o partículas.",
      ],
      image: {
        src: THREE_LAYER_IMAGE,
        alt: "Sección transversal de malla sinterizada de 3 capas",
      },
    },
    specTable: {
      heading: "Especificaciones estándar de 3 capas",
      intro: "Configuraciones en AISI 316L y 304L para filtración de gases y líquidos industriales.",
      columns: [
        { key: "specification", label: "Especificación", align: "left" },
        { key: "nominalRating", label: "Retención nominal (µm)", align: "right" },
        { key: "controlMesh", label: "Malla de control", align: "right" },
        { key: "nominalThickness", label: "Espesor nominal (mm)", align: "right" },
        { key: "layerOrder", label: "Secuencia de capas", align: "left" },
      ],
      rows: [
        {
          specification: "3L-005",
          nominalRating: "5",
          controlMesh: "Tejido reps",
          nominalThickness: "1.70",
          layerOrder: "Protección + Control (Reps) + Soporte base",
        },
        {
          specification: "3L-010",
          nominalRating: "10",
          controlMesh: "Tejido reps",
          nominalThickness: "1.70",
          layerOrder: "Protección + Control (Reps) + Soporte base",
        },
        {
          specification: "3L-025",
          nominalRating: "25",
          controlMesh: "500 Mesh",
          nominalThickness: "1.70",
          layerOrder: "Protección (100M) + Control (500M) + Soporte (20M)",
        },
        {
          specification: "3L-050",
          nominalRating: "50",
          controlMesh: "300 Mesh",
          nominalThickness: "1.70",
          layerOrder: "Protección (80M) + Control (300M) + Soporte (20M)",
        },
        {
          specification: "3L-100",
          nominalRating: "100",
          controlMesh: "150 Mesh",
          nominalThickness: "1.70",
          layerOrder: "Protección (60M) + Control (150M) + Soporte (16M)",
        },
      ],
      caption: "Espesores entre 0.8 mm y 3.5 mm configurables con diferentes mallas base.",
    },
    features: {
      heading: "Ventajas técnicas y operativas",
      intro: "Configuración equilibrada de baja resistencia al flujo y soporte mecánico seguro.",
      items: [
        {
          title: "Alta permeabilidad de flujo",
          text: "Menos capas internas reducen la resistencia, facilitando caudales superiores con mínima pérdida de carga.",
        },
        {
          title: "Porosidad estable",
          text: "La difusión térmica asegura que las aberturas no se deformen ante fluctuaciones de presión del proceso.",
        },
        {
          title: "Excelente retrolavado",
          text: "La estructura más delgada previene la retención profunda de sólidos, permitiendo limpiezas ágiles.",
        },
        {
          title: "Conformado versátil",
          text: "Se corta, pliega, enrolla y suelda fácilmente para formar cartuchos cilíndricos y discos filtrantes.",
        },
      ],
    },
    related: {
      heading: "Construcciones sinterizadas afines",
      intro: "Compare la estructura de 3 capas con las opciones de 5 capas y multicapa reforzada.",
      cards: [
        {
          title: "Malla multicapa sinterizada",
          text: "Familia principal que abarca construcciones estándar y personalizadas unidas por difusión.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "Laminado de malla de alambre multicapa sinterizado al vacío",
          },
          badge: "Familia principal",
          linkLabel: "Ver resumen multicapa",
        },
        {
          title: "Malla sinterizada estándar de 5 capas",
          text: "La configuración universal que combina capa protectora, control, dispersión y refuerzo dual.",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "Estructura de malla sinterizada estándar de 5 capas",
          },
          badge: "5 capas estándar",
          linkLabel: "Ver detalles de 5 capas",
        },
        {
          title: "Malla compuesta multicapa",
          text: "Mallas multicapa reforzadas diseñadas para soportar picos severos de presión diferencial.",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "Vista macro de laminado de malla de acero inoxidable multicapa sinterizado",
          },
          badge: "Refuerzo pesado",
          linkLabel: "Ver detalles multicapa",
        },
      ],
    },
    cta: {
      id: "three-layer-sintered-cta",
      eyebrow: "Fabricación a medida",
      heading: "Solicite su malla sinterizada de 3 capas",
      lead: "Fabricamos láminas, discos estampados y cartuchos filtrantes de 3 capas sinterizadas según sus requerimientos precisos.",
      secondaryLabel: "Contactar con ingenieros",
      secondaryTo: "/ContactPage",
      note: "Aleaciones AISI 316L y 304L con certificados de ensayo disponibles según pedido.",
    },
  },
  de: {
    breadcrumbKey: "ThreeLayerSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Mehrlagen-Sintergewebe",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "3-lagiges Sintergewebe",
      lead: "Aufgebaut aus äußerem Schutzgewebe, präziser Filtrationskontrollschicht und mechanischem Stützgewebe, für ausgewogene Permeabilität und zuverlässige Stabilität.",
      secondaryCta: {
        label: "Mehrlagen-Übersicht",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: THREE_LAYER_IMAGE,
        alt: "Querschnitt eines 3-lagigen Edelstahl-Sintergewebelaminats",
      },
      cardNote: "3-Lagen-Schichtaufbau",
      cardLink: {
        label: "Spezifikationen anfragen",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Feinheit", value: "2 – 100 µm" },
        { label: "Standardlegierungen", value: "AISI 316L / 304L" },
        { label: "Nenndicke", value: "1.70 mm (0.8 – 3.5 mm)" },
        { label: "Aufbau", value: "Kontroll- + Schutz- + Stützlage" },
      ],
    },
    overview: {
      eyebrow: "3-Stufen-Konstruktion",
      heading: "Hochpermeable 3-Lagen-Filtrationsmatrix",
      paragraphs: [
        "Das 3-lagige Sinterdrahtgewebe wurde für Prozesse entwickelt, bei denen hohe Durchflussleistung und ein geringer Anfangsdifferenzdruck neben mikrometergenauer Partikelabscheidung im Vordergrund stehen.",
        "Die äußere Schutzlage fängt gröbere Partikel ab und schirmt die Feingewebelage vor mechanischem Verschleiß ab; die Kontrollschicht sichert Trennschärfen von 2 µm bis 100 µm; und die rückseitige Stützlage sorgt für die notwendige Steifigkeit bei der Fluidabfuhr.",
        "Das Festkörper-Diffusionsglühen verschweißt alle Drahtknoten zu einer starren Einheit, die auch zyklischen Rückspülvorgängen ohne Lagenablösung standhält.",
      ],
      keyPoints: [
        "Äußeres Schutzgewebe schützt die empfindliche mikrometergenaue Kontrolllage.",
        "Optimierter 3-Lagen-Aufbau gewährleistet höhere Permeabilität als kompaktere Mehrlagenplatten.",
        "Festkörperdiffusion verhindert Bindemittelkontamination und Faserabrieb.",
      ],
      image: {
        src: THREE_LAYER_IMAGE,
        alt: "Querschnitt eines 3-lagigen Edelstahl-Sintergewebelaminats",
      },
    },
    specTable: {
      heading: "Standard-Spezifikationen für 3-Lagen-Gewebe",
      intro: "Konfigurationen in AISI 316L und 304L für Flüssigkeits- und Gasfiltrationsanlagen.",
      columns: [
        { key: "specification", label: "Spezifikation", align: "left" },
        { key: "nominalRating", label: "Nennfeinheit (µm)", align: "right" },
        { key: "controlMesh", label: "Filtergewebe-Masche", align: "right" },
        { key: "nominalThickness", label: "Nenndicke (mm)", align: "right" },
        { key: "layerOrder", label: "Lagenreihenfolge", align: "left" },
      ],
      rows: [
        {
          specification: "3L-005",
          nominalRating: "5",
          controlMesh: "Tressengewebe",
          nominalThickness: "1.70",
          layerOrder: "Schutzlage + Kontrolllage (Tresse) + Stützlage",
        },
        {
          specification: "3L-010",
          nominalRating: "10",
          controlMesh: "Tressengewebe",
          nominalThickness: "1.70",
          layerOrder: "Schutzlage + Kontrolllage (Tresse) + Stützlage",
        },
        {
          specification: "3L-025",
          nominalRating: "25",
          controlMesh: "500 Mesh",
          nominalThickness: "1.70",
          layerOrder: "Schutz (100M) + Filter (500M) + Stütze (20M)",
        },
        {
          specification: "3L-050",
          nominalRating: "50",
          controlMesh: "300 Mesh",
          nominalThickness: "1.70",
          layerOrder: "Schutz (80M) + Filter (300M) + Stütze (20M)",
        },
        {
          specification: "3L-100",
          nominalRating: "100",
          controlMesh: "150 Mesh",
          nominalThickness: "1.70",
          layerOrder: "Schutz (60M) + Filter (150M) + Stütze (16M)",
        },
      ],
      caption: "Dicken von 0.8 mm bis 3.5 mm sind durch alternative Stützgewebe konfigurierbar.",
    },
    features: {
      heading: "Konstruktive und betriebliche Vorteile",
      intro: "Fokussierter Schichtaufbau für geringen Strömungswiderstand bei sicherer mechanischer Abstützung.",
      items: [
        {
          title: "Hohe Strömungsdurchlässigkeit",
          text: "Geringere Schichtanzahl minimiert innere Reibung und erzielt hohe Durchsätze bei minimalem Druckverlust.",
        },
        {
          title: "Porenformbeständigkeit",
          text: "Diffusionsgebundene Kreuzungspunkte bleiben auch bei Druckstößen und Lastwechseln unverrückbar stabil.",
        },
        {
          title: "Effiziente Rückspülung",
          text: "Der kompakte Aufbau verhindert Tiefeneinlagerungen und begünstigt eine gründliche Oberflächenabreinigung.",
        },
        {
          title: "Vielseitig formbar",
          text: "Problemlos schneidbar, stanzbar, rollbar und schweißbar zu Kerzen, Ronden und Konusfiltern.",
        },
      ],
    },
    related: {
      heading: "Verwandte Sintergewebetypen",
      intro: "Vergleichen Sie 3-Lagen-Laminate mit 5-lagigen Standardaufbauten und Mehrlagenverbunden.",
      cards: [
        {
          title: "Mehrlagen-Sinterdrahtgewebe",
          text: "Die übergeordnete Produktfamilie diffusionsgebundener Schichtverbunde.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "Vakuum-diffusionsgebundenes Mehrlagen-Sinterdrahtgewebelaminat",
          },
          badge: "Hauptfamilie",
          linkLabel: "Zur Mehrlagen-Übersicht",
        },
        {
          title: "5-lagiges Standard-Sintergewebe",
          text: "Die universelle Industriekonfiguration mit Schutz-, Filter-, Dispersions- und zwei Stützlagen.",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "Struktur eines 5-lagigen Standard-Sintermetallgewebes",
          },
          badge: "5-Lagen-Standard",
          linkLabel: "5-Lagen-Details",
        },
        {
          title: "Mehrlagen-Verbundsintergewebe",
          text: "Hochbelastbare Spezialverbunde mit Lochblechverstärkung für anspruchsvolle Trennaufgaben.",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "Makroansicht eines mehrlagigen gesinterten Edelstahldrahtgewebes",
          },
          badge: "Verstärkter Verbund",
          linkLabel: "Mehrlagen-Details",
        },
      ],
    },
    cta: {
      id: "three-layer-sintered-cta",
      eyebrow: "Sonderanfertigung",
      heading: "3-Lagen-Sintergewebe konfigurieren",
      lead: "Wir fertigen 3-Lagen-Sinterbleche, Ronden und Zylinderfilter nach Ihren maßlichen und verfahrenstechnischen Vorgaben.",
      secondaryLabel: "Ingenieure kontaktieren",
      secondaryTo: "/ContactPage",
      note: "Erhältlich in AISI 316L und 304L mit vollständigen Werkszeugnissen auf Bestellung.",
    },
  },
  fr: {
    breadcrumbKey: "ThreeLayerSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Toile multicouche frittée",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Toile frittée 3 couches",
      lead: "Composée d'une toile protectrice, d'une couche filtrante micrométrique et d'un support rigide, alliant perméabilité élevée et tenue mécanique fiable.",
      secondaryCta: {
        label: "Aperçu multicouche",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: THREE_LAYER_IMAGE,
        alt: "Coupe transversale d'un stratifié fritté à 3 couches en acier inoxydable",
      },
      cardNote: "Stratifié 3 couches",
      cardLink: {
        label: "Demander les spécifications",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Seuil", value: "2 – 100 µm" },
        { label: "Nuances standard", value: "AISI 316L / 304L" },
        { label: "Épaisseur nominale", value: "1.70 mm (0.8 – 3.5 mm)" },
        { label: "Structure", value: "Contrôle + Protection + Support" },
      ],
    },
    overview: {
      eyebrow: "Structure à 3 étages",
      heading: "Matrice de filtration 3 couches à haute perméabilité",
      paragraphs: [
        "La toile frittée 3 couches est spécifiquement élaborée pour les procédés réclamant une perméabilité élevée et une faible perte de charge initiale tout en assurant une filtration micrométrique fiable.",
        "La couche externe préserve la toile fine contre les chocs et particules grossières ; la couche centrale contrôle la coupure de 2 µm à 100 µm ; et la couche inférieure assure la rigidité mécanique nécessaire pour l'évacuation du filtrat.",
        "La liaison par diffusion sous vide solidarise tous les fils métalliques, permettant d'affronter des flux directs réguliers et des cycles de contre-lavage intenses sans risque de délamination.",
      ],
      keyPoints: [
        "Toile extérieure de protection protégeant la couche filtrante micrométrique.",
        "Structure à 3 niveaux offrant une perméabilité supérieure aux plaques plus denses.",
        "La diffusion à l'état solide exclut tout risque de relargage particulaire ou d'adhésif.",
      ],
      image: {
        src: THREE_LAYER_IMAGE,
        alt: "Coupe transversale d'un stratifié fritté à 3 couches en acier inoxydable",
      },
    },
    specTable: {
      heading: "Spécifications standard 3 couches",
      intro: "Nuances AISI 316L et 304L pour filtration industrielle de liquides et de gaz.",
      columns: [
        { key: "specification", label: "Spécification", align: "left" },
        { key: "nominalRating", label: "Seuil nominal (µm)", align: "right" },
        { key: "controlMesh", label: "Toile filtrante", align: "right" },
        { key: "nominalThickness", label: "Épaisseur nominale (mm)", align: "right" },
        { key: "layerOrder", label: "Empilement des couches", align: "left" },
      ],
      rows: [
        {
          specification: "3L-005",
          nominalRating: "5",
          controlMesh: "Toile reps",
          nominalThickness: "1.70",
          layerOrder: "Protection + Contrôle (Reps) + Support de base",
        },
        {
          specification: "3L-010",
          nominalRating: "10",
          controlMesh: "Toile reps",
          nominalThickness: "1.70",
          layerOrder: "Protection + Contrôle (Reps) + Support de base",
        },
        {
          specification: "3L-025",
          nominalRating: "25",
          controlMesh: "500 Mesh",
          nominalThickness: "1.70",
          layerOrder: "Protection (100M) + Contrôle (500M) + Support (20M)",
        },
        {
          specification: "3L-050",
          nominalRating: "50",
          controlMesh: "300 Mesh",
          nominalThickness: "1.70",
          layerOrder: "Protection (80M) + Contrôle (300M) + Support (20M)",
        },
        {
          specification: "3L-100",
          nominalRating: "100",
          controlMesh: "150 Mesh",
          nominalThickness: "1.70",
          layerOrder: "Protection (60M) + Contrôle (150M) + Support (16M)",
        },
      ],
      caption: "Épaisseurs de 0.8 mm à 3.5 mm configurables avec différentes toiles de support.",
    },
    features: {
      heading: "Avantages techniques et opérationnels",
      intro: "Agencement rationnel associant faible résistance à l'écoulement et soutien mécanique robuste.",
      items: [
        {
          title: "Perméabilité supérieure",
          text: "Moins d'interfaces internes réduisent la résistance hydraulique pour des débits élevés à faible pression.",
        },
        {
          title: "Stabilité des ouvertures",
          text: "Les nœuds soudés par diffusion restent immobiles même sous variations répétées de charge.",
        },
        {
          title: "Régénération efficace",
          text: "La finesse du profil limite le colmatage profond et facilite le nettoyage par flux inverse.",
        },
        {
          title: "Aptitude au formage",
          text: "Se découpe, s'emboutit, se roule et se soude pour fabriquer cartouches, paniers et disques de filtration.",
        },
      ],
    },
    related: {
      heading: "Structures frittées associées",
      intro: "Comparez les toiles 3 couches avec les solutions 5 couches et multicouches renforcées.",
      cards: [
        {
          title: "Toile métallique multicouche frittée",
          text: "Gamme principale regroupant nos solutions standards et sur mesure frittées sous vide.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "Stratifié de toile métallique multicouche frittée sous vide",
          },
          badge: "Gamme mère",
          linkLabel: "Voir l'aperçu multicouche",
        },
        {
          title: "Toile frittée standard 5 couches",
          text: "La configuration universelle associant protection, contrôle, dispersion et double armature.",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "Structure de toile métallique frittée 5 couches standard",
          },
          badge: "5 couches standard",
          linkLabel: "Détails 5 couches",
        },
        {
          title: "Toile composite multicouche",
          text: "Composites renforcés avec tôle perforée pour absorber les fortes variations de charge.",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "Vue macro d'un stratifié multicouche fritté en acier inoxydable",
          },
          badge: "Renfort lourd",
          linkLabel: "Détails multicouche",
        },
      ],
    },
    cta: {
      id: "three-layer-sintered-cta",
      eyebrow: "Fabrication spéciale",
      heading: "Commandez votre toile frittée 3 couches",
      lead: "MutualFilter usine vos panneaux 3 couches, disques et cylindres filtrants selon vos seuils de coupure et dimensions.",
      secondaryLabel: "Consulter nos spécialistes",
      secondaryTo: "/ContactPage",
      note: "Nuances AISI 316L et 304L avec certificats métallurgiques délivrés sur demande de commande.",
    },
  },
  pt: {
    breadcrumbKey: "ThreeLayerSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Malha multicamadas sinterizada",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malha sinterizada de 3 camadas",
      lead: "Desenvolvida com malha externa de proteção, camada de controle micrométrico e tela de suporte estrutural, equilibrando alta vazão e suporte mecânico confiável.",
      secondaryCta: {
        label: "Visão geral multicamadas",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: THREE_LAYER_IMAGE,
        alt: "Corte transversal de malha sinterizada de 3 camadas em aço inoxidável",
      },
      cardNote: "Laminado de 3 camadas",
      cardLink: {
        label: "Solicitar dados técnicos",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Retenção", value: "2 – 100 µm" },
        { label: "Ligas padrão", value: "AISI 316L / 304L" },
        { label: "Espessura nominal", value: "1.70 mm (0.8 – 3.5 mm)" },
        { label: "Estrutura", value: "Controle + Proteção + Suporte" },
      ],
    },
    overview: {
      eyebrow: "Construção de 3 estágios",
      heading: "Matriz de filtração de 3 camadas com alta permeabilidade",
      paragraphs: [
        "A malha sinterizada de 3 camadas é recomendada para processos onde alta vazão e baixo diferencial inicial de pressão são requisitos centrais ao lado de retenção micrométrica.",
        "A camada externa de proteção atua contra impactos e desgaste prematuro; a camada central de controle determina a retenção exata de 2 µm a 100 µm; e a tela de suporte inferior garante resistência mecânica para o fluxo contínuo de processo.",
        "A fusão atômica por difusão a vácuo une firmemente as malhas metálicas, suportando fluxo direto contínuo e pulsos de retrolavagem sem alteração dos poros.",
      ],
      keyPoints: [
        "Malha externa de proteção protege a camada de controle micrométrico.",
        "Configuração de 3 níveis oferece menor resistência e maior permeabilidade hidráulica.",
        "A fusão por difusão atômica descarta desprendimento de filamentos ou ligantes.",
      ],
      image: {
        src: THREE_LAYER_IMAGE,
        alt: "Corte transversal de malha sinterizada de 3 camadas em aço inoxidável",
      },
    },
    specTable: {
      heading: "Especificações padrão de 3 camadas",
      intro: "Disponível nas ligas AISI 316L e 304L para sistemas de filtração de líquidos e gases.",
      columns: [
        { key: "specification", label: "Especificação", align: "left" },
        { key: "nominalRating", label: "Retenção nominal (µm)", align: "right" },
        { key: "controlMesh", label: "Malha de controle", align: "right" },
        { key: "nominalThickness", label: "Espessura nominal (mm)", align: "right" },
        { key: "layerOrder", label: "Sequência de camadas", align: "left" },
      ],
      rows: [
        {
          specification: "3L-005",
          nominalRating: "5",
          controlMesh: "Trama holandesa",
          nominalThickness: "1.70",
          layerOrder: "Proteção + Controle (Holandesa) + Tela base",
        },
        {
          specification: "3L-010",
          nominalRating: "10",
          controlMesh: "Trama holandesa",
          nominalThickness: "1.70",
          layerOrder: "Proteção + Controle (Holandesa) + Tela base",
        },
        {
          specification: "3L-025",
          nominalRating: "25",
          controlMesh: "500 Mesh",
          nominalThickness: "1.70",
          layerOrder: "Proteção (100M) + Controle (500M) + Suporte (20M)",
        },
        {
          specification: "3L-050",
          nominalRating: "50",
          controlMesh: "300 Mesh",
          nominalThickness: "1.70",
          layerOrder: "Proteção (80M) + Controle (300M) + Suporte (20M)",
        },
        {
          specification: "3L-100",
          nominalRating: "100",
          controlMesh: "150 Mesh",
          nominalThickness: "1.70",
          layerOrder: "Proteção (60M) + Controle (150M) + Suporte (16M)",
        },
      ],
      caption: "Espessuras entre 0.8 mm e 3.5 mm podem ser configuradas com malhas de suporte alternativas.",
    },
    features: {
      heading: "Diferenciais técnicos e operacionais",
      intro: "Arquitetura equilibrada com reduzida resistência ao fluxo e apoio mecânico confiável.",
      items: [
        {
          title: "Alta permeabilidade de fluxo",
          text: "Menor número de camadas internas diminui a perda de carga e viabiliza vazões maiores por área.",
        },
        {
          title: "Geometria estável de poros",
          text: "A difusão sólida mantém a retenção uniforme mesmo sob variações de pressão da linha.",
        },
        {
          title: "Retrolavagem eficiente",
          text: "A menor profundidade do leito previne retenção de partículas profundas, agilizando a limpeza.",
        },
        {
          title: "Versatilidade de fabricação",
          text: "Permite cortes precisos, estampagem de discos, calandragem e soldagem de tubos filtrantes.",
        },
      ],
    },
    related: {
      heading: "Construções sinterizadas relacionadas",
      intro: "Compare o desempenho de 3 camadas com as opções de 5 camadas e multicamadas reforçadas.",
      cards: [
        {
          title: "Malha multicamadas sinterizada",
          text: "Família principal que engloba estruturas sinterizadas de 3 e 5 camadas e modelos especiais.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "Laminado de malha de arame multicamadas sinterizado a vácuo",
          },
          badge: "Linha principal",
          linkLabel: "Ver visão geral multicamadas",
        },
        {
          title: "Malha sinterizada padrão de 5 camadas",
          text: "A configuração universal que combina proteção, controle, dispersão e dois reforços estruturais.",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "Estrutura de malha sinterizada padrão de 5 camadas",
          },
          badge: "5 camadas padrão",
          linkLabel: "Ver detalhes de 5 camadas",
        },
        {
          title: "Malha composta multicamadas",
          text: "Compostos reforçados projetados para absorver picos severos de pressão diferencial.",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "Visão macro de laminado multicamadas sinterizado de aço inoxidável",
          },
          badge: "Reforço pesado",
          linkLabel: "Ver detalhes multicamadas",
        },
      ],
    },
    cta: {
      id: "three-layer-sintered-cta",
      eyebrow: "Projetos sob medida",
      heading: "Solicite cotação de malha sinterizada de 3 camadas",
      lead: "Fornecemos chapas, discos circulares e cartuchos tubulares de 3 camadas nas dimensões e faixas de retenção solicitadas.",
      secondaryLabel: "Falar com engenharia",
      secondaryTo: "/ContactPage",
      note: "Produzidas em aços inoxidáveis AISI 316L e 304L com documentação técnica sob especificação do pedido.",
    },
  },
};
